import { z } from "zod";

/**
 * Le document est la source de verite : la geometrie n'est jamais stockee, elle
 * est recalculee depuis cet arbre. Trois surfaces ecrivent dedans — la timeline,
 * l'editeur de code et la couche IA — et c'est ce qui rend les trois possibles.
 *
 * Toute modification de la forme de ce fichier impose d'incrementer
 * SCHEMA_VERSION et d'ajouter la montee de version correspondante dans
 * `migrateDocument`. Des pieces existent en base : on ne casse pas un format.
 */
export const SCHEMA_VERSION = 2;

/** Une cote : un nombre, ou une formule utilisant les parametres du document. */
const expressionSchema = z.union([z.number().finite(), z.string().min(1).max(200)]);

const vec3Schema = z.tuple([expressionSchema, expressionSchema, expressionSchema]);

const parameterSchema = z.object({
  name: z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/),
  expression: expressionSchema,
  comment: z.string().max(300).optional(),
});

const featureIdSchema = z.string().min(1).max(64);

const featureBaseSchema = z.object({
  id: featureIdSchema,
  name: z.string().min(1).max(120),
  /** Une feature supprimee reste dans l'arbre mais ne produit rien. */
  suppressed: z.boolean().optional(),
});

/**
 * Chaque feature produit un ou plusieurs corps, ranges sous son propre `id`.
 * Les operations booleennes consomment les corps qu'elles citent : le resultat
 * du document est l'ensemble des corps qui n'ont ete consommes par personne. Ce
 * choix garde l'evaluation lineaire et le cache trivial a invalider — on rejoue
 * depuis la premiere feature modifiee, pas depuis le debut.
 *
 * Toutes les features ci-dessous rendent un corps unique, sauf `decoupe`, qui
 * en rend autant que le debit compte de morceaux. Ce qui cite un groupe s'y
 * applique corps par corps.
 */
export const boxFeatureSchema = featureBaseSchema.extend({
  type: z.literal("box"),
  width: expressionSchema,
  depth: expressionSchema,
  height: expressionSchema,
  origin: vec3Schema.optional(),
  /** Centre la boite sur son origine au lieu de la poser par son coin. */
  centered: z.boolean().optional(),
});

export const cylinderFeatureSchema = featureBaseSchema.extend({
  type: z.literal("cylinder"),
  radius: expressionSchema,
  height: expressionSchema,
  origin: vec3Schema.optional(),
  /** Axe de revolution, normalise a l'evaluation. Z par defaut. */
  axis: vec3Schema.optional(),
});

export const sphereFeatureSchema = featureBaseSchema.extend({
  type: z.literal("sphere"),
  radius: expressionSchema,
  origin: vec3Schema.optional(),
});

export const booleanFeatureSchema = featureBaseSchema.extend({
  type: z.literal("boolean"),
  op: z.enum(["union", "subtract", "intersect"]),
  targetId: featureIdSchema,
  toolIds: z.array(featureIdSchema).min(1),
});

/**
 * Debite un corps trop grand pour le plateau, et pose les liaisons qui le
 * remontent. La regle est une grille : sur chaque axe, autant de tranches
 * egales qu'il en faut pour tenir dans le plateau moins la marge. Les cellules
 * vides sont jetees — un cadre debite en 2 x 3 rend six segments de bandeau,
 * pas six blocs.
 *
 * Les cotes du tourillon sont **explicites** et non implicites, contrairement a
 * l'origine d'une boite : ce sont des cotes de fabrication. Qui reprend une
 * piece six mois plus tard doit lire le diametre perce dans le document, pas le
 * deduire d'un defaut enfoui dans le noyau.
 */
export const decoupeFeatureSchema = featureBaseSchema.extend({
  type: z.literal("decoupe"),
  targetId: featureIdSchema,
  /** Volume imprimable de la machine, en millimetres. */
  plateau: vec3Schema,
  /** Retiree du plateau sur chaque axe : jupe, adherence, gauchissement. */
  marge: expressionSchema,
  liaisons: z.enum(["aucune", "tourillons"]),
  /** Diametre nominal. Le percage vaut ce diametre plus le jeu, le tourillon
   *  imprime le vaut moins le jeu : un tourillon hetre du commerce entre donc
   *  dans le meme percage. */
  tourillonDiametre: expressionSchema,
  tourillonLongueur: expressionSchema,
  tourillonJeu: expressionSchema,
  /** Epaisseur de matiere exigee autour d'un percage. Rien n'est perce dans une
   *  nervure trop mince pour tenir. */
  paroiMini: expressionSchema,
});

export const transformFeatureSchema = featureBaseSchema.extend({
  type: z.literal("transform"),
  targetId: featureIdSchema,
  translate: vec3Schema.optional(),
  /** Rotations en degres, appliquees dans l'ordre X puis Y puis Z. */
  rotate: vec3Schema.optional(),
});

export const featureSchema = z.discriminatedUnion("type", [
  boxFeatureSchema,
  cylinderFeatureSchema,
  sphereFeatureSchema,
  booleanFeatureSchema,
  transformFeatureSchema,
  decoupeFeatureSchema,
]);

export const documentSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(200),
  /** Le millimetre est la seule unite : on imprime, on ne fait pas de genie civil. */
  units: z.literal("mm"),
  parameters: z.array(parameterSchema).max(500),
  features: z.array(featureSchema).max(2000),
});

export type Vec3 = z.infer<typeof vec3Schema>;
export type BoxFeature = z.infer<typeof boxFeatureSchema>;
export type CylinderFeature = z.infer<typeof cylinderFeatureSchema>;
export type SphereFeature = z.infer<typeof sphereFeatureSchema>;
export type BooleanFeature = z.infer<typeof booleanFeatureSchema>;
export type TransformFeature = z.infer<typeof transformFeatureSchema>;
export type DecoupeFeature = z.infer<typeof decoupeFeatureSchema>;
export type Feature = z.infer<typeof featureSchema>;
export type HomecadDocument = z.infer<typeof documentSchema>;

/** Les types de feature qui consomment d'autres corps plutot que d'en creer un. */
export function consumedFeatureIds(feature: Feature): string[] {
  switch (feature.type) {
    case "boolean":
      return [feature.targetId, ...feature.toolIds];
    case "transform":
    case "decoupe":
      return [feature.targetId];
    default:
      return [];
  }
}

export function createEmptyDocument(id: string, name: string): HomecadDocument {
  return { schemaVersion: SCHEMA_VERSION, id, name, units: "mm", parameters: [], features: [] };
}

export class DocumentError extends Error {}

/**
 * Monte un document d'une version anterieure vers la version courante. Les
 * montees s'enchainent : une piece en v1 traverse `de1vers2`, puis la suivante
 * quand elle existera. Des pieces existent en base, elles doivent toutes
 * pouvoir remonter jusqu'ici.
 */
export function migrateDocument(raw: unknown): HomecadDocument {
  if (typeof raw !== "object" || raw === null) {
    throw new DocumentError("Document illisible");
  }
  let candidat: object = raw;
  if ((candidat as { schemaVersion?: unknown }).schemaVersion === 1) {
    candidat = de1vers2(candidat);
  }
  const version = (candidat as { schemaVersion?: unknown }).schemaVersion;
  if (version !== SCHEMA_VERSION) {
    throw new DocumentError(
      `Version de document non prise en charge : ${String(version)} (attendu ${SCHEMA_VERSION})`,
    );
  }
  return parseDocument(candidat);
}

/**
 * 1 vers 2 : la v2 ajoute la feature « decoupe ». Aucun champ existant ne
 * change et aucune feature de la v1 ne se relit autrement, la montee ne fait
 * donc que reetiqueter. Elle est ecrite quand meme, et separement du controle
 * de version : la suivante s'ajoutera a sa suite sans y toucher.
 */
function de1vers2(document: object): object {
  return { ...document, schemaVersion: 2 };
}

export function parseDocument(raw: unknown): HomecadDocument {
  const parsed = documentSchema.safeParse(raw);
  if (!parsed.success) {
    throw new DocumentError(`Document invalide : ${parsed.error.issues[0]?.message ?? "forme inattendue"}`);
  }
  assertReferencesResolve(parsed.data);
  return parsed.data;
}

/**
 * Une feature ne peut citer qu'un corps deja produit au-dessus d'elle. Sans
 * cette verification, un document reordonne par l'interface ou ecrit par un LLM
 * peut decrire un cycle, et l'evaluateur boucle au lieu de refuser proprement.
 */
function assertReferencesResolve(document: HomecadDocument): void {
  // Les noms servent aux messages : « cite Edge_3 » ne dit rien a qui reordonne
  // la timeline, « cite Percage central » designe la ligne a redescendre.
  const names = new Map(document.features.map((feature) => [feature.id, feature.name]));
  const seen = new Set<string>();
  for (const feature of document.features) {
    if (seen.has(feature.id)) {
      throw new DocumentError(`Identifiant de feature en double : ${feature.id}`);
    }
    for (const referenced of consumedFeatureIds(feature)) {
      if (!seen.has(referenced)) {
        const name = names.get(referenced);
        throw new DocumentError(
          name === undefined
            ? `La feature « ${feature.name} » cite ${referenced}, qui n'existe pas`
            : `La feature « ${feature.name} » cite « ${name} », qui doit rester au-dessus d'elle`,
        );
      }
    }
    seen.add(feature.id);
  }
}
